import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../csr-web-api.service';
import { AuthSessionService } from './auth-session.service';
import { MandatoryFeedbackService } from './mandatory-feedback.service';

function normalizePath(url: string): string {
  return (url.split('?')[0] ?? '').split('#')[0] ?? '';
}

function isLegalExemptPath(url: string): boolean {
  const path = normalizePath(url);
  return (
    path === '/learner/legal' ||
    path.startsWith('/learner/legal/') ||
    path === '/account/delete'
  );
}

function isProfileExemptPath(url: string): boolean {
  const path = normalizePath(url);
  return (
    path === '/account/complete-profile' ||
    path.startsWith('/account/complete-profile/') ||
    isLegalExemptPath(url)
  );
}

function challengeIdFromUrl(url: string): string | null {
  const match = url.split('?')[0]?.match(/^\/career-challenges\/([^/]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Authentication and required legal/profile information are the only global
 * navigation gates. Onboarding state is not a gate: learners may explore all
 * learner areas, while screens explain unavailable actions in context.
 */
export const learnerAuthGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthSessionService);
  const router = inject(Router);
  const api = inject(CsrWebApiService);
  const mandatoryFeedback = inject(MandatoryFeedbackService);
  const challengeId = challengeIdFromUrl(state.url);
  const challengeIntent = _route.queryParamMap.get('challengeIntent');

  if (!auth.isAuthenticated() || !auth.isLearner()) {
    // Resolve challenge access after sign-in; URLs can identify private packs.
    if (challengeId) {
      return router.createUrlTree(['/learner/login'], {
        queryParams: {
          ...(challengeIntent ? { challengeIntent } : {}),
          returnUrl: state.url,
        },
      });
    }
    return router.createUrlTree(['/learner/login']);
  }
  if (isLegalExemptPath(state.url)) return true;

  try {
    if (await auth.ensureLegalAcceptanceResolved()) {
      return router.createUrlTree(['/learner/legal'], {
        queryParams: {
          returnUrl: state.url,
          ...(challengeIntent ? { challengeIntent } : {}),
        },
      });
    }
  } catch {
    return router.createUrlTree(['/learner/legal'], {
      queryParams: {
        returnUrl: state.url,
        ...(challengeIntent ? { challengeIntent } : {}),
      },
    });
  }

  try {
    const profile = await firstValueFrom(api.getLearnerProfile());
    if (
      (!profile.onboardingStatus || profile.onboardingStatus === 'none') &&
      !isProfileExemptPath(state.url)
    ) {
      return router.createUrlTree(['/account/complete-profile'], {
        queryParams: {
          returnUrl: state.url,
          ...(challengeIntent ? { challengeIntent } : {}),
        },
      });
    }
    if (profile.pendingFeedback?.schemas?.length) {
      void mandatoryFeedback.openFeedbackDialog(profile.pendingFeedback);
    }
  } catch {
    // Allow the requested page to surface its own loading or error message.
  }
  return true;
};
